import { EVENT, RSVP_URL } from "@/lib/eventData";
import { Calendar, Clock, MapPin } from "lucide-react";

export default function Hero() {
  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <section
      id="overview"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-20 pb-24 sm:pt-24 sm:pb-16"
    >
      {/* No local background image — the App-level fixed Havana frame
          shows through. Just a soft radial vignette to anchor the title. */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(212,175,55,0.10),transparent_55%)]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
        <div className="lg:col-span-8 rise">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 border border-[#D4AF37]/40 rounded-full mb-6 sm:mb-8 bg-[#123A2E]/50 backdrop-blur max-w-full">
            <span className="w-1.5 h-1.5 rounded-full bg-[#F3E08A] animate-pulse shrink-0" />
            <span
              data-testid="hero-badge"
              className="font-display text-[9px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] text-[#F3E08A] leading-tight"
            >
              Tolcon Year-End Function 2026
            </span>
          </div>

          <h1
            data-testid="hero-title"
            className="font-serif-luxe text-[2.75rem] sm:text-6xl lg:text-8xl font-bold tracking-tight leading-[0.95]"
          >
            <span className="block">Havana</span>
            <span className="inline-block gold-shimmer italic pr-2 sm:pr-4 pb-2 sm:pb-3 leading-[1.1] overflow-visible">Nights</span>
          </h1>

          <p
            data-testid="hero-subtitle"
            className="mt-5 sm:mt-6 max-w-2xl text-sm sm:text-lg text-[#E0D5B8] font-serif-luxe italic leading-relaxed"
          >
            {EVENT.subtitle}
          </p>

          <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4">
            <a
              data-testid="hero-cta-rsvp"
              href={RSVP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold px-6 sm:px-8 py-3.5 rounded-sm text-[12px] text-center"
            >
              Confirm Attendance
            </a>
            <button
              data-testid="hero-cta-program"
              onClick={() => scrollTo("#program")}
              className="btn-ghost-gold px-6 sm:px-8 py-3.5 rounded-sm text-[12px]"
            >
              View Program
            </button>
            <button
              data-testid="hero-cta-menu"
              onClick={() => scrollTo("#menu")}
              className="btn-ghost-gold px-6 sm:px-8 py-3.5 rounded-sm text-[12px]"
            >
              Explore Menu
            </button>
          </div>
        </div>

        {/* Event details card */}
        <div className="lg:col-span-4 rise" style={{ animationDelay: "180ms" }}>
          <div className="card-luxe gold-frame p-6 sm:p-8 lg:p-10 relative">
            <div className="deco-divider mb-5 sm:mb-6">
              <span className="font-display text-[10px] tracking-[0.3em] text-[#F3E08A]">
                Save the Date
              </span>
            </div>

            <div className="space-y-5 sm:space-y-6">
              <Row
                icon={<Calendar size={16} />}
                label="Date"
                value={EVENT.date}
                testid="hero-detail-date"
              />
              <Row
                icon={<Clock size={16} />}
                label="Time"
                value={EVENT.time}
                testid="hero-detail-time"
              />
              <Row
                icon={<MapPin size={16} />}
                label="Venue"
                value={
                  <>
                    <div>{EVENT.venue}</div>
                    <div className="text-[#A5BCB1] text-xs mt-0.5">
                      {EVENT.address}
                    </div>
                  </>
                }
                testid="hero-detail-venue"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll cue — hidden on small screens so it doesn't overlap CTAs */}
      <div className="hidden sm:flex absolute bottom-6 inset-x-0 justify-center opacity-70">
        <div className="flex flex-col items-center gap-2">
          <div className="w-px h-10 bg-gradient-to-b from-transparent to-[#D4AF37]" />
          <span className="font-display text-[9px] tracking-[0.4em] text-[#9F7E30]">
            Scroll
          </span>
        </div>
      </div>
    </section>
  );
}

function Row({ icon, label, value, testid }) {
  return (
    <div className="flex gap-4">
      <div className="mt-1 w-8 h-8 grid place-items-center border border-[#D4AF37]/50 text-[#F3E08A] rounded-sm shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-display text-[9px] tracking-[0.3em] text-[#9F7E30] mb-1">
          {label}
        </div>
        <div data-testid={testid} className="text-[#F8F1DC] text-sm sm:text-base">
          {value}
        </div>
      </div>
    </div>
  );
}
